import { useEffect, useRef, useState } from "react";

const fmt = (s) => {
  if (!Number.isFinite(s)) return "0:00";
  const m = Math.floor(s / 60);
  return `${m}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
};

export default function VideoProgress({ videoRef, active }) {
  const fill = useRef(null);
  const track = useRef(null);
  const [drag, setDrag] = useState(null);

  useEffect(() => {
    if (!active) {
      if (fill.current) fill.current.style.transform = "scaleX(0)";
      return;
    }
    let raf;
    const tick = () => {
      const v = videoRef.current;
      if (v && v.duration && fill.current) fill.current.style.transform = `scaleX(${v.currentTime / v.duration})`;
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active, videoRef]);

  const seek = (e) => {
    const v = videoRef.current;
    const r = track.current.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (e.clientX - r.left) / r.width));
    if (v?.duration) {
      v.currentTime = ratio * v.duration;
      fill.current.style.transform = `scaleX(${ratio})`;
    }
    return { ratio, time: ratio * (v?.duration || 0), duration: v?.duration || 0 };
  };

  const down = (e) => {
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    setDrag(seek(e));
  };
  const move = (e) => { if (drag) setDrag(seek(e)); };
  const up = (e) => {
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    setDrag(null);
  };

  return (
    <div ref={track} onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up} onClick={(e) => e.stopPropagation()}
      className="group absolute inset-x-0 bottom-0 z-30 flex h-4 cursor-pointer touch-none items-end" data-testid="video-progress-bar"
      role="slider" aria-label="Video progress" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round((drag?.ratio || 0) * 100)}>
      {drag && drag.duration > 0 && (
        <span className="pointer-events-none absolute bottom-32 left-1/2 -translate-x-1/2 rounded-sm bg-black/60 px-3 py-1.5 font-mono text-base tabular-nums text-white"
          data-testid="video-progress-time">
          {fmt(drag.time)} <span className="text-white/50">/ {fmt(drag.duration)}</span>
        </span>
      )}
      <div className={`relative w-full bg-white/20 transition-[height] duration-150 ${drag ? "h-1" : "h-[2px] group-hover:h-1"}`}>
        <div ref={fill} className="absolute inset-0 origin-left bg-white will-change-transform" style={{ transform: "scaleX(0)" }} data-testid="video-progress-fill" />
        {drag && (
          <span className="absolute top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white shadow"
            style={{ left: `${drag.ratio * 100}%` }} />
        )}
      </div>
    </div>
  );
}
